<template>
  <main class="reset-page">
    <section class="reset-card" aria-labelledby="reset-title">
      <img class="maix-wordmark" :src="maixWordmark" alt="Maix">

      <div v-if="status === 'loading'" class="status-panel" aria-live="polite">
        <div class="spinner reset-spinner" aria-hidden="true"></div>
        <p>Checking your reset link…</p>
      </div>

      <div v-else-if="status === 'verified'" class="status-panel" aria-live="polite">
        <h1 id="reset-title">Email verified.</h1>
        <p>Return to your dashboard's authenticator setup and select “I have verified my email.”</p>
      </div>

      <div v-else-if="status === 'invalid'" class="status-panel" aria-live="polite">
        <p class="eyebrow">Password reset</p>
        <h1 id="reset-title">This link is no longer available.</h1>
        <p>{{ pageError }}</p>
        <a class="primary-action" :href="returnUrl">{{ returnLabel }}</a>
      </div>

      <div v-else-if="status === 'success'" class="status-panel" aria-live="polite">
        <div class="success-mark" aria-hidden="true">✓</div>
        <p class="eyebrow">Password updated</p>
        <h1 id="reset-title">Your new password is ready.</h1>
        <p>{{ isAdminSetup ? "You can now sign in to the admin dashboard with your new password." : "You can now return to Maix and sign in with your new password." }}</p>
        <a class="primary-action" :href="returnUrl">{{ returnLabel }}</a>
      </div>

      <form v-else class="reset-form" @submit.prevent="submitPasswordReset">
        <p class="eyebrow">Secure account access</p>
        <h1 id="reset-title">Create a new password.</h1>
        <p class="intro">
          Enter a new password for <strong>{{ maskedEmail }}</strong>.
        </p>

        <label for="new-password">New password</label>
        <input
          id="new-password"
          v-model="password"
          :disabled="status === 'submitting'"
          type="password"
          autocomplete="new-password"
          aria-describedby="password-requirements"
          minlength="8"
          required
        >
        <div id="password-requirements" class="password-requirements">
          <p>Password must include:</p>
          <ul>
            <li>A minimum of eight characters</li>
            <li>One uppercase letter</li>
            <li>One lowercase letter</li>
            <li>One number</li>
            <li>One special character</li>
            <li>Must not begin with llf (reserved for account setup)</li>
          </ul>
        </div>

        <label for="confirm-password">Confirm new password</label>
        <input
          id="confirm-password"
          v-model="confirmedPassword"
          :disabled="status === 'submitting'"
          type="password"
          autocomplete="new-password"
          minlength="8"
          required
        >

        <p v-if="formError" class="form-error" role="alert">{{ formError }}</p>

        <button class="primary-action" type="submit" :disabled="!canSubmit">
          <span v-if="status === 'submitting'" class="spinner" aria-hidden="true"></span>
          <span>{{ status === 'submitting' ? 'UPDATING PASSWORD' : 'RESET PASSWORD' }}</span>
        </button>

        <p class="security-note">
          Maix will never ask you to send your password by email or share it with support staff.
        </p>
      </form>
    </section>

    <a class="site-link" href="https://www.askmaix.com">www.askmaix.com</a>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { projectAuth } from "@/firebase/firebase";
import maixWordmark from "@/assets/maix-wordmark.png";

const isAdminSetup = new URLSearchParams(window.location.search).get("adminSetup") === "true";
const returnUrl = isAdminSetup ? "https://www.lomaxlawconnect.com" : "https://www.askmaix.com";
const returnLabel = isAdminSetup ? "ADMIN SIGN IN" : "RETURN TO MAIX";
const status = ref("loading");
const accountEmail = ref("");
const actionCode = ref("");
const password = ref("");
const confirmedPassword = ref("");
const pageError = ref("");
const formError = ref("");
const passwordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$@#!%*?&]).{8,}$/;

const maskedEmail = computed(() => {
  const [localPart, domain] = accountEmail.value.split("@");
  if (!localPart || !domain) { return "your Maix account"; }

  const firstCharacter = localPart.slice(0, 1);
  return `${firstCharacter}${"•".repeat(Math.max(3, localPart.length - 1))}@${domain}`;
});

const passwordMeetsRequirements = computed(() => passwordPattern.test(password.value) && !/^llf/i.test(password.value));

const canSubmit = computed(() => status.value === "ready" &&
  passwordMeetsRequirements.value &&
  password.value === confirmedPassword.value);

const invalidLinkMessage = "The password-reset link may have expired or already been used. Return to Maix and request a new password-reset email.";

onMounted(async () => {
  document.title = "Reset Password | Maix";

  const parameters = new URLSearchParams(window.location.search);
  const mode = parameters.get("mode");
  actionCode.value = parameters.get("oobCode") ?? "";

  if (mode === "verifyEmail" && actionCode.value) {
    document.title = "Verify Email | Maix";
    try {
      await applyActionCode(projectAuth, actionCode.value);
      status.value = "verified";
    } catch {
      pageError.value = "This verification link is invalid or expired. Request a new email from authenticator setup.";
      status.value = "invalid";
    }
    return;
  }

  if (mode !== "resetPassword" || !actionCode.value) {
    pageError.value = invalidLinkMessage;
    status.value = "invalid";
    return;
  }

  try {
    accountEmail.value = await verifyPasswordResetCode(projectAuth, actionCode.value);
    status.value = "ready";
  } catch {
    pageError.value = invalidLinkMessage;
    status.value = "invalid";
  }
});

const submitPasswordReset = async () => {
  formError.value = "";

  if (password.value !== confirmedPassword.value) {
    formError.value = "The passwords do not match.";
    return;
  }

  if (!passwordMeetsRequirements.value) {
    formError.value = "Use a password that meets all of the listed requirements.";
    return;
  }

  status.value = "submitting";

  try {
    await confirmPasswordReset(projectAuth, actionCode.value, password.value);
    password.value = "";
    confirmedPassword.value = "";
    status.value = "success";
  } catch (error) {
    if (error?.code === "auth/weak-password") {
      formError.value = "That password does not meet the account security requirements.";
      status.value = "ready";
      return;
    }

    pageError.value = invalidLinkMessage;
    status.value = "invalid";
  }
};
</script>

<style scoped>
.reset-page {
  min-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: 40px 18px;
  background: linear-gradient(135deg, #168ab5 0%, #0b5274 45%, #082f4f 100%);
  color: #405566;
}

.reset-card {
  width: min(100%, 560px);
  box-sizing: border-box;
  padding: 42px 46px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 20px 56px rgba(3, 31, 51, 0.3);
}

.maix-wordmark {
  display: block;
  width: 150px;
  height: auto;
  margin-bottom: 34px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #168ab5;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 18px;
  color: #0b3555;
  font-size: clamp(34px, 6vw, 44px);
  font-weight: 700;
  line-height: 1.08;
}

.intro,
.status-panel p {
  margin: 0 0 26px;
  font-size: 17px;
  line-height: 1.55;
}

.reset-form label {
  display: block;
  margin: 18px 0 7px;
  color: #0b3555;
  font-size: 16px;
  font-weight: 700;
}

.reset-form input {
  width: 100%;
  box-sizing: border-box;
  padding: 13px 15px;
  border: 1px solid #b8c9d3;
  border-radius: 11px;
  background: #f8fbfc;
  color: #0b3555;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 17px;
  outline: none;
}

.reset-form input:focus {
  border-color: #168ab5;
  box-shadow: 0 0 0 3px rgba(22, 138, 181, 0.17);
}

.password-requirements {
  margin: 10px 0 0;
  color: #71818d;
  font-size: 13px;
  line-height: 1.45;
}

.password-requirements p {
  margin: 0 0 4px;
}

.password-requirements ul {
  margin: 0;
  padding-left: 19px;
}

.form-error {
  margin: 18px 0 0;
  padding: 12px 14px;
  border-left: 4px solid #b33b32;
  border-radius: 8px;
  background: #fff1ef;
  color: #7d2721;
  font-size: 14px;
  line-height: 1.45;
}

.primary-action {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 26px;
  padding: 14px 22px;
  border: 0;
  border-radius: 999px;
  background: #f6bc05;
  color: #0b3555;
  font-family: "Dosis", Avenir, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

.primary-action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.security-note {
  margin: 24px 0 0;
  padding-top: 20px;
  border-top: 1px solid #dbe4e9;
  color: #71818d;
  font-size: 13px;
  line-height: 1.5;
}

.status-panel {
  text-align: left;
}

.success-mark {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  margin-bottom: 24px;
  border-radius: 50%;
  background: #2a7978;
  color: #ffffff;
  font-size: 30px;
  font-weight: 700;
}

.reset-spinner {
  width: 28px;
  height: 28px;
  margin-bottom: 18px;
  border-color: rgba(11, 53, 85, 0.18);
  border-top-color: #168ab5;
}

.site-link {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 600px) {
  .reset-page {
    justify-content: flex-start;
    padding: 18px 12px 28px;
  }

  .reset-card {
    padding: 32px 24px;
    border-radius: 16px;
  }

  .maix-wordmark {
    width: 140px;
    margin-bottom: 28px;
  }
}
</style>
